import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Injectable, inject } from "@angular/core";
import { FileService } from "./file.service";
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '@environments/environment';
import { ServiceResult } from '@shared/helper/service-result';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { HttpBaseService } from '../base_services/http_base.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CheckUserStateBody, userRegisterModel } from '@shared/model/customerModels/register-view-model';
import { ChangeCurrentPasswordModel } from '@shared/model/ChangeCurrentPassword-model';
import { FCMService } from '../firebase_services/fcm.service';
import { ForgetPasswordModel, ReqForgetPassword } from '@shared/model/forgetPassword-model';
import { UserStateEnum } from '@shared/enums/user-state.enum';
import { ISignInRequest } from '@shared/model/sign-in.model';
import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_KEY_CLIENT, EXPIRE_IN_KEY, REFRESH_TOKEN_KEY } from 'constants/storage-keys';
import { ERequestModify, IChangeEmail, IChangeNumber, IRequestChange, IRequestSet, ISetEmail, ISetPass, IVerification } from '@shared/model/profile/edit-profile.model';
import { SigninResponse } from '@shared/model/auth/sign-in.model';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { SnackBarTypes } from '@shared/components/cutome-snack-bar/custome-snack-bar.component';
import { SnackbarService } from '@shared/utils/snackbar.service';


@Injectable({
  providedIn: "root"
})
export class SigninService extends HttpBaseService {
  repo: LocalRepository;
  private fileService = inject(FileService);
  private firebaseService = inject(FCMService);
  private googleAuth = inject(SocialAuthService);

  private snackbarService = inject(SnackbarService);



  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, '');
    this.repo = repo;

    this.jwtHelper = new JwtHelperService();
  }

  // ? get client_access_token for firebase
  signinClient(): Observable<SigninResponse | null> {
    if (this.repo.IsInBrowser) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        })
      };
      const data = `client_id=api.angular.client&client_secret=secret&grant_type=client_credentials`;
      return this.http.post<SigninResponse>(`${environment.AuthServicesUrl}/connect/token`, data, httpOptions).pipe(
        switchMap(result => {
          if (result?.access_token) {
            this.Repository.setCookieItem(ACCESS_TOKEN_KEY_CLIENT, result?.access_token);
            // return this.firebaseService.requestPermission();
          }
          return of(result);
        }),
      catchError(err => {
        console.log(err);
        return of(null);
      })
      );
    }
    else return of(null);
  }

  // ? return true if flow is succesful
  signin(credentials: ISignInRequest): Observable<boolean> {
    this.fileService.userImage_$.next('');
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      })
    };
    const data = this.buildSignInData(credentials);
    return this.http.post<SigninResponse>(`${environment.AuthServicesUrl}/connect/token`, data, httpOptions).pipe(
      map(res => {
        if (res.access_token) {
          this.repo.resetCredentials(res?.access_token, res.refresh_token ?? '', res.expires_in?.toString() ?? '');
          return true;
        }
        else {
          this.snackbarService.openSnackbar(SnackBarTypes.error, 'اطلاعات وارد شده صحیح نیست. لطفا دوباره تلاش کنید');
          return false;
        }
      }),
      catchError(err => {
        console.log(err);
        return of(false)
      })
    );
  }

  requestForPhoneNumberVerification(phoneNumber: string): Observable<TServiceResult<string>> {

    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/account/RequestForPhoneNumberVerification`, { phoneNumber })
  }

  requestForEmailVerification(email: string): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/account/RequestForEmailVerification`, { email })
  }

  phoneNumberVerification(verifyCode: number, phoneNumber: string): Observable<ServiceResult> {
    const verify = {
      phoneNumber: phoneNumber,
      code: verifyCode.toString(),
    };
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/account/PhoneNumberVerification`, verify)
  }

  emailVerification(verifyCode: number, phoneNumber: string): Observable<ServiceResult> {
    const verify = {
      email: phoneNumber,
      code: verifyCode.toString(),
    };
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/EmailVerification`, verify)
  }

  checkValidUserName(userName: string): Observable<TServiceResult<boolean>> {
    return this.http.get<TServiceResult<boolean>>(`${this.authenticationUrl}/api/account/CheckDuplicateUsername/${userName}`);
  }

  checkUserExist(user: CheckUserStateBody): Observable<TServiceResult<UserStateEnum>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    return this.http.post<TServiceResult<UserStateEnum>>(`${this.authenticationUrl}/api/account/CheckIfExistsAndConfirmed`, user, httpOptions);
  }

  signOut() {
    this.repo.clearUserData();
    this.googleAuth.signOut();
    this.signinClient().subscribe();
  }


  refreshToken(): Observable<SigninResponse> {

    const rft = this.Repository.getCookieItem(REFRESH_TOKEN_KEY);

    if (rft) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        })
      };
      let data = `grant_type=refresh_token&refresh_token=${rft}`;
      data = "client_id=api.angular.client&client_secret=secret&" + data;
      return this.http.post<SigninResponse>(`${this.authenticationUrl}/connect/token`, data, httpOptions).pipe(
        map(result => {
          if (result?.access_token) {
            this.Repository.setCookieItem(ACCESS_TOKEN_KEY, result?.access_token);
            this.Repository.setCookieItem(EXPIRE_IN_KEY, result?.expires_in!.toString());
            this.Repository.setCookieItem(REFRESH_TOKEN_KEY, result.refresh_token ?? '');
          }
          return result;
        })
      );

    }
    else {
      this.repo.clearUserData();
      return of({});
    }
  }

  forgetPassword(forgetMethd: ReqForgetPassword): Observable<TServiceResult<number>> {
    if (forgetMethd.phoneNumber) {
      return this.http.post<TServiceResult<number>>(`${environment.AuthServicesUrl}/api/account/ForgetPassword`, { phoneNumber: forgetMethd.phoneNumber })
    }
    else {
      return this.http.post<TServiceResult<number>>(`${environment.AuthServicesUrl}/api/account/ForgetPassword`, { email: forgetMethd.email })
    }
  }

  resetPassword(forgetPassword: ForgetPasswordModel): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/account/ConfirmForgottenPassword`, forgetPassword);
  }

  changeCurrentPassword(changeCurrentPass: ChangeCurrentPasswordModel): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/account/ChangePassword`, changeCurrentPass)
  }

  registerWithGmail(user: userRegisterModel): Observable<ServiceResult> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/account/RegisterWithGmailAuthentication`, user, httpOptions);
  }


  requestForChangePhoneOrEmailVerification(value: IRequestChange): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/Account/RequestForChangePhoneOrEmailVerification`, value);
  }

  changePhoneNumber(value: IChangeNumber): Observable<ServiceResult> {
    return this.http.patch<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/ChangePhoneNumber`, value)
  }

  phoneNumberOrEmailVerification(value: IVerification): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/PhoneNumberOrEmailVerification`, value);
  }

  setPhoneNumber(value: ISetEmail): Observable<ServiceResult> {
    return this.http.patch<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/SetPhoneNumber`, value);
  }

  removePhoneNumber(code: string): Observable<ServiceResult> {
    return this.http.delete<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/RemovePhoneNumber/${code}`);
  }

  requestForRemovePhoneOrEmailVerification(type: ERequestModify): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/Account/RequestForRemovePhoneOrEmailVerification/${type}`, null);
  }

  requestForSetPhoneOrEmailVerification(value: IRequestSet): Observable<TServiceResult<string>> {
    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/Account/RequestForSetPhoneOrEmailVerification`, value)
  }

  setEmail(value: ISetEmail): Observable<ServiceResult> {
    return this.http.patch<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/SetEmail`, value);
  }

  changeEmail(value: IChangeEmail): Observable<ServiceResult> {
    return this.http.patch<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/ChangeEmail`, value)
  }

  removeEmail(code: string): Observable<ServiceResult> {
    return this.http.delete<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/RemoveEmail/${code}`);
  }

  getOtp(input: string): Observable<TServiceResult<string>> {
    const body = { input };
    return this.http.post<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/Account/Otp`, body);
  }


  private buildSignInData(credentials: ISignInRequest) {
    let data = `client_id=api.angular.client&client_secret=secret&grant_type=${credentials.grandType}&input_type=${credentials.input_type}`;

    if (!credentials.input_type) {
      data += `&username=${credentials.username}&password=${credentials.password}`
    }

    else {
      data += `&input=${credentials.input}&verification_token=${credentials.verificationCode}`
    }

    if (credentials.name || credentials.username) {
      data += `&name=${credentials.name}&user_name=${credentials.username}`;
    }
    return data;
  }

  getOtpCode(): Observable<TServiceResult<string>> {
    return this.http.get<TServiceResult<string>>(`${environment.AuthServicesUrl}/api/Account/SendChangePasswordOtpCode`)
  }

  otpValidation(code: string): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(`${environment.AuthServicesUrl}/api/Account/ValidateChangePasswordOtpCode/${code}`, {})
  }

  setPassword(data: ISetPass): Observable<ServiceResult> {
    return this.http.put<ServiceResult>(`${environment.AuthServicesUrl}/api/account/ChangePasswordByOtpCode`, data)
  }
}

